import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Chip, TableCell, TableRow, Typography } from '@mui/material';
import { GenderAgeDisplay, MaskedEmailDisplay, NameNicknameDisplay, TalkStatusBadge } from '../common';
import { FAMILY_RELATIONSHIP_TYPES, getCancerHistoriesArray, getCancerTypeName, Patient, ROLE_COLORS, ROLE_TYPES } from '../../types/patient';

interface PatientTableRowProps {
  patient: Patient;
}

const PatientTableRow: React.FC<PatientTableRowProps> = ({patient}) => {
  const navigate = useNavigate();
  // ロール名を日本語に変換（家族の場合は関係性も表示）
  const getRoleLabel = (roleType: string, relationshipType?: string | null) => {
    const baseLabel = ROLE_TYPES[roleType as keyof typeof ROLE_TYPES] || roleType;
    // 家族の場合は関係性も表示
    if (roleType === 'FAMILY' && relationshipType) {
      const relationshipLabel = FAMILY_RELATIONSHIP_TYPES[relationshipType as keyof typeof FAMILY_RELATIONSHIP_TYPES];
      if (relationshipLabel) {
        return `${baseLabel}（${relationshipLabel}）`;
      }
    }
    return baseLabel;
  };
  return (
    <TableRow
      hover
      key={patient.id}
      onClick={(event) => {
        // 特定の子要素がクリックされた場合はイベント伝播をスキップし、詳細に遷移しない
        if ((event.target as HTMLElement).closest('.no-propagation')) {
          return;
        }
        navigate(`/patients/${patient.id}`);
      }}
      sx={theme => ({
        cursor: 'pointer', // クリック可能なことを示すカーソル
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
        ...(patient.resigned_at ? {
          backgroundColor: '#fcf5f5', // 退会済みユーザーはごく薄い赤に
          boxShadow: 'inset 0 0 5px rgba(220, 220, 220, 0.5)', // 少し影を付ける
          opacity: 0.8, // 少し透明に
          '& td': {color: '#777'}, // テキストは少し薄く
          borderLeft: '3px solid #ffcccc', // 左側に目印の線
        } : {})
      })}
    >
      <TableCell>
        {/* 対話状態の表示（共通コンポーネント） */}
        <TalkStatusBadge
          isActive={!!patient.in_active_talk}
          isResigned={!!patient.resigned_at}
        />
      </TableCell>
      <TableCell>
        <Chip
          label={getRoleLabel(patient.role_type, patient.family_relationship_type)}
          size="small"
          style={{
            backgroundColor: ROLE_COLORS[patient.role_type as keyof typeof ROLE_COLORS] || '#757575',
            color: 'white',
            fontWeight: 'bold',
            opacity: patient.resigned_at ? 0.6 : 1 // 退会済みはロール表示も少し薄く
          }}
        />
      </TableCell>
      <TableCell>
        <NameNicknameDisplay
          name={patient.name}
          nickname={patient.nickname}
          nameColor={patient.resigned_at ? '#666' : undefined}
          nicknameColor={patient.resigned_at ? '#888' : 'text.secondary'}
          isTestAccount={patient.is_test_account}
        />
      </TableCell>
      <TableCell>
        <GenderAgeDisplay
          genderType={patient.gender_type}
          birthday={patient.birthday}
        />
      </TableCell>
      <TableCell>
        {(() => {
          try {
            // getCancerHistoriesArrayを使用してデータを配列に変換
            const histories = getCancerHistoriesArray(patient.cancer_histories);
            if (!histories || histories.length === 0) {
              return <Typography variant="body2" color="text.secondary">なし</Typography>;
            }
            // 各がん種類をマッピングして取得
            const cancerTypes = histories
            .filter(h => h && typeof h === 'object')
            .map(h => {
              const cancerType = h.cancer_type || '';
              // がん種類の表示名
              let displayName = '';
              // 'OTHER'の場合はotherフィールドを使用
              if (cancerType === 'OTHER' && h.other) {
                // 7文字以上なら省略
                displayName = h.other.length > 7 ?
                  `${h.other.substring(0, 7)}...` : h.other;
              } else {
                displayName = getCancerTypeName(cancerType);
              }
              // 診断日の処理
              let diagnosisDate = '(不明)';
              if (h.year || h.month) {
                const year = h.year || '??';
                const month = h.month || '??';
                diagnosisDate = `(${year}/${month})`;
              }
              return `${displayName} ${diagnosisDate}`;
            })
            .filter(Boolean); // 空の値を除外
            if (cancerTypes.length > 0) {
              return <Typography variant="body2">{cancerTypes.join('、')}</Typography>;
            }
            return <Typography variant="body2" color="text.secondary">なし</Typography>;
          } catch(e) {
            return <Typography variant="body2" color="error">表示エラー</Typography>;
          }
        })()}
      </TableCell>
      <TableCell>
        <MaskedEmailDisplay email={patient.email} isBold={false}/>
      </TableCell>
    </TableRow>
  );
};
export default PatientTableRow;
